import { elevation } from '../elevation';

const lightElevation = {
  0: 'none',
  1: '0 0 0 1px rgba(0, 0, 0, 0.06) inset',
  2: '0px 1px 2px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04) inset',
  3: '0px 2px 4px rgba(0, 0, 0, 0.06), 0px 8px 8px -8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04) inset',
  4: '0px 2px 4px rgba(0, 0, 0, 0.08), 0px 8px 16px -4px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05) inset',
  5: '0px 4px 8px rgba(0, 0, 0, 0.08), 0px 16px 32px -8px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06) inset',
};

const colors = {
  surface: {
    ink: '#171717',
    onPrimary: '#fafafa',
    body: '#525252',
    mute: '#888888',
    hairline: '#eaeaea',
    hairlineStrong: '#d4d4d4',
    canvas: '#fafafa',
    canvasSoft: '#ffffff',
    canvasSoft2: '#f5f5f5',
    link: '#0761d1',
    linkDeep: '#1d4ed8',
    linkBgSoft: '#eff6ff',
    error: '#ee0000',
    errorSoft: '#fef2f2',
    errorDeep: '#c50000',
    warning: '#d97706',
    warningSoft: '#fffbeb',
    warningDeep: '#ab570a',
    violet: '#9333ea',
    violetSoft: '#f5f3ff',
    violetDeep: '#7928ca',
    cyan: '#0d9488',
    cyanSoft: '#f0fdfa',
    cyanDeep: '#29bc9b',
    highlightPink: '#eb367f',
    highlightMagenta: '#db2777',
    selectionBg: '#171717',
    selectionFg: '#fafafa',
    gradientDevelopStart: '#007cf0',
    gradientDevelopEnd: '#00dfd8',
    gradientPreviewStart: '#7928ca',
    gradientPreviewEnd: '#ff0080',
    gradientShipStart: '#ff4d4d',
    gradientShipEnd: '#f9cb28',
  },
  primary: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a1a1a1',
    500: '#171717',
    600: '#262626',
    700: '#333333',
    800: '#525252',
    900: '#fafafa',
  },
  brand: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b7cf8',
    600: '#6A5CFF',
    700: '#5a4ce6',
    800: '#4a3dcc',
    900: '#322899',
  },
  secondary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  success: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#29bc9b',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },
  bookmark: { saved: '#D4A017', savedMuted: '#B8860B' },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f5a623',
    600: '#d97706',
    700: '#ab570a',
    800: '#92400e',
    900: '#78350f',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ff5555',
    600: '#ee0000',
    700: '#c50000',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#3b82f6',
    500: '#3291ff',
    600: '#0761d1',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  accent: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#3b82f6',
    500: '#3291ff',
    600: '#0761d1',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a1a1a1',
    500: '#888888',
    600: '#525252',
    700: '#333333',
    800: '#262626',
    900: '#171717',
  },
  background: {
    primary: '#fafafa',
    secondary: '#ffffff',
    tertiary: '#f5f5f5',
    card: '#ffffff',
    elevated: '#ffffff',
  },
  border: {
    light: '#eaeaea',
    medium: '#d4d4d4',
    dark: '#a1a1a1',
    accent: '#171717',
  },
  text: {
    primary: '#171717',
    secondary: '#525252',
    tertiary: '#888888',
    muted: '#888888',
    inverse: '#fafafa',
    accent: '#0761d1',
  },
  sidebar: {
    bg: '#ffffff',
    activeBg: '#f5f5f5',
    activeIndicator: '#171717',
    inactiveText: '#525252',
    activeText: '#171717',
    sectionHeader: '#888888',
    badgeBg: '#171717',
    logoIconBg: '#171717',
    userCardBg: '#f5f5f5',
    separator: '#eaeaea',
  },
  header: {
    bg: '#ffffff',
    searchBg: '#f5f5f5',
    searchPlaceholder: '#888888',
    border: '#eaeaea',
  },
  mainContent: { bg: '#fafafa' },
  map: {
    ocean: '#f5f5f5',
    land: '#d4d4d4',
    landHover: '#a1a1a1',
    stroke: '#eaeaea',
    shimmerBase: '#f5f5f5',
    shimmerMid: '#e5e5e5',
    shimmerHighlight: '#d4d4d4',
  },
  elevation: lightElevation,
};

const cssVariables = {
  '--cs-bg-primary': colors.background.primary,
  '--cs-bg-secondary': colors.background.secondary,
  '--cs-bg-tertiary': colors.background.tertiary,
  '--cs-bg-card': colors.background.card,
  '--cs-bg-elevated': colors.background.elevated,
  '--cs-border-light': colors.border.light,
  '--cs-border-medium': colors.border.medium,
  '--cs-border-dark': colors.border.dark,
  '--cs-border-accent': colors.border.accent,
  '--cs-text-primary': colors.text.primary,
  '--cs-text-secondary': colors.text.secondary,
  '--cs-text-tertiary': colors.text.tertiary,
  '--cs-text-muted': colors.text.muted,
  '--cs-text-inverse': colors.text.inverse,
  '--cs-text-accent': colors.text.accent,
  '--cs-canvas': colors.surface.canvas,
  '--cs-canvas-soft': colors.surface.canvasSoft,
  '--cs-canvas-soft-2': colors.surface.canvasSoft2,
  '--cs-ink': colors.surface.ink,
  '--cs-hairline': colors.surface.hairline,
  '--cs-link': colors.surface.link,
  '--cs-link-deep': colors.surface.linkDeep,
  '--cs-selection-bg': colors.surface.selectionBg,
  '--cs-selection-fg': colors.surface.selectionFg,
  '--cs-primary-50': colors.primary[50],
  '--cs-primary-100': colors.primary[100],
  '--cs-primary-200': colors.primary[200],
  '--cs-primary-300': colors.primary[300],
  '--cs-primary-400': colors.primary[400],
  '--cs-primary-500': colors.primary[500],
  '--cs-primary-600': colors.primary[600],
  '--cs-primary-700': colors.primary[700],
  '--cs-primary-800': colors.primary[800],
  '--cs-primary-900': colors.primary[900],
  '--cs-brand-500': colors.brand[500],
  '--cs-brand-600': colors.brand[600],
  '--cs-gradient-develop': 'rgba(0, 124, 240, 0.12)',
  '--cs-gradient-preview': 'rgba(255, 0, 128, 0.08)',
  '--cs-gradient-cyan': 'rgba(80, 227, 194, 0.08)',
  '--cs-gradient-ship': 'rgba(249, 203, 40, 0.06)',
  '--cs-nav-blur-bg': 'rgba(250, 250, 250, 0.85)',
  '--cs-scrollbar-thumb': 'rgba(0, 0, 0, 0.15)',
  '--cs-scrollbar-thumb-hover': 'rgba(0, 0, 0, 0.25)',
  '--cs-map-ocean': colors.map.ocean,
  '--cs-map-land': colors.map.land,
};

export default { colors, cssVariables };
